import type { ErrorObject } from 'ajv';
import type { google as googleMaps } from 'google-maps';
import { User } from 'firebase/auth';

import type { AppliedPromotion, Promotion } from '@pedix-workspace/shared-promotions';

// eslint-disable-next-line no-var
declare var google: googleMaps;

export enum TypesOfShippings {
  TAKE_AWAY = 'TAKE_AWAY',
  DELIVERY = 'DELIVERY',
  IN_PLACE = 'IN_PLACE',
  PARCEL = 'PARCEL',
}

export type AppUserCustomClaims = {
  appSuperAdmin?: boolean;
  appInvitedUser?: boolean;
};

export type UserDataWithClaims = Pick<User, 'uid' | 'displayName' | 'photoURL' | 'email'> & {
  claims: AppUserCustomClaims;
};

export type AuthProvider = 'password' | 'google.com' | 'facebook.com'; // phone, twitter.com, github.com, apple.com, yahoo.com, hotmail.com
export type InvitedUser = {
  id?: string;
  establishmentId: string;
  status: 'pending' | 'accepted';
  uid?: string;
  details: {
    name: string;
    email: string;
    phone?: string;
    jobTitle?: string;
    roles: InvitedUserRole[];
    invitationDate: Date;
    invitedBy: string;
    authProvider?: AuthProvider;
    created: Date;
    updated: Date;
  };
};

export const invitedUserRoles = [
  'ESTABLISHMENT_ADMIN',
  'GENERAL_INFO_ADMIN',
  'CATALOG_ADMIN',
  'PAYMENT_ADMIN',
  'SHIPPING_ADMIN',
  'CUSTOM_FIELDS_ADMIN',
  'COUPONS_ADMIN',
  'ORDERS_ADMIN',
  'ORDERS_EDITOR_ADMIN',
  'INVENTORY_ADMIN',
  'PROMOTIONS_ADMIN',
  'BILLING_ADMIN',
  'STATISTICS_ADMIN',
  'INTEGRATIONS_ADMIN',
  'ANALYTICS_ADMIN',
  'ADS_ADMIN',
  'WHATSAPP_BOT_ADMIN',
  'CASHIER_ADMIN',
  'SUPPLY_ADMIN',
  'TABLES_ADMIN',
] as const;

export type InvitedUserRole = (typeof invitedUserRoles)[number];

export type SubsidiarySelectionSource = 'query' | 'cookie' | 'last-order' | 'cookie-auto';

export interface Establishment {
  id: string;
  created?: Date; // NOTE: this was added on 2024 and no backfill was used for old data
  updated?: Date; // NOTE: this was added on 2024 and no backfill was used for old data
  logo: string;
  name: string;
  slug: string;
  userId: string;
  userIds: Record<string, InvitedUserRole[]>;
  phone: string;
  additionalPhone?: string;
  /**
   * Teléfonos del titular en E.164 (derivado de phone/additionalPhone/contactInfo.phone),
   * para el lookup inverso teléfono→establishment. Lo mantiene un trigger; no se edita a mano.
   */
  ownerPhonesE164?: string[];
  image?: string; // for subsidiaries
  theme?: EstablishmentTheme;
  subsidiaries: string[];
  subsidiarySelector?: boolean;
  subsidiaryParent?: string;
  subsidiaryGroup?: string;
  subsidiaryRedirectEnabled?: boolean;
  mapPinImageUrl?: string;
  cloneTargets?: string[];
  clonedFrom?: string;
  // @deprecated
  address: string;
  addressDetails?: AddressDetails;
  addressReferences?: string;
  currencyCode: CurrencyCode;
  altLanguage: ALT_LANGUAGES;
  categories: string[];
  isDigitalMenu: boolean;
  countryCode?: Country;
  countryDetail?: string;
  contactInfo: ContactInfo;
  subscription?: SubscriptionInfo;
  gracePeriodDays?: number | null;
  workingDays?: WorkingDays;
  temporaryClosure?: TemporaryClosure;
  activeInventoryCount?: ActiveInventoryCount;
  suppliesConfiguration?: SuppliesConfiguration;
  facebookPixel?: string;
  googleAnalytics?: string;
  googleTagManager?: string;
  facebookDomainVerification?: string;
  seoSocialMedia?: EstablishmentSeoSocialMedia;
  features: Feature[];
  featureTrials?: FeatureTrial[];
  links?: {
    website: string;
    instagram: string;
    facebook: string;
    twitter: string;
    tiktok: string;
    returnPolicy?: string;
    privacyPolicy?: string;
    termsOfService?: string;
    withdrawalButton?: string;
  };
  productsExport?: EstablishmentProductsExport;
  subdomainStatus?: 'PENDING' | 'ACTIVE' | 'INACTIVE';
  subdomainRedirection?: boolean;
  customDomain?: CustomDomain;
  redirectTo?: string;
  catalogConfiguration: CatalogConfiguration;
  announcements?: Announcements;
  // Food Bank Donation
  enableFoodBankDonation?: boolean;
  facebookCatalog?: {
    catalogId: string;
    productSetId: string;
    customConversionId: string;
    hasCatalog: boolean;
    lastExecution: Date;
    error: string;
    catalogUrl: string;
  };
  googleMerchant?: {
    csvUrl: string;
    feedId: string;
    lastExecution?: Date;
  };
  maxMultimediaRepositorySize: number;
  security: EstablishmentSecurity;
  onboarding?: EstablishmentOnboarding;
}

export type OnboardingCompletedVia = 'wizard' | 'guard' | 'checklist';

export const tourIds = [
  'share_store',
  'cash_register_first_use',
  'online_payment',
  'stock',
  'inventory_count',
  'supplies',
  'costs',
  'guide_security',
  'guide_image_repository',
  'guide_order_center',
  'guide_billing',
  'guide_measurement',
  'guide_advertising',
  'guide_product_tags',
  'guide_working_hours',
  'guide_bulk_edit',
  'guide_bulk_edit_items',
  'guide_invited_users',
  'guide_shared_options',
  'guide_custom_fields',
  'guide_qr',
  'guide_seo_social',
  'guide_coupons',
  'guide_catalog_clone',
  'guide_delivery_zones',
  'guide_theme',
  'guide_announcements',
  'guide_catalog',
  'guide_catalog_products',
  'guide_general_info',
  'guide_reports',
  'guide_subscription',
  'guide_shipping',
  'guide_payment_methods',
  'guide_promotions',
  'guide_catalog_settings',
  'guide_supplies_upsell',
] as const;

export type TourId = (typeof tourIds)[number];

export const mergedTourIds: Readonly<Record<string, TourId>> = {
  first_order: 'guide_order_center',
  order_center: 'guide_order_center',
  coupons: 'guide_coupons',
  coupon_create: 'guide_coupons',
  billing: 'guide_billing',
  measurement: 'guide_measurement',
  store_setup: 'guide_general_info',
  store_qr: 'guide_qr',
  catalog_base: 'guide_catalog',
  catalog_enrich: 'guide_catalog',
  catalog_products: 'guide_catalog_products',
};

export const tourStateFields = ['seenAt', 'completedAt', 'dismissedAt', 'reofferedAt'] as const;

export type TourState = Partial<Record<(typeof tourStateFields)[number], Date | null>>;

export type TourStates = Partial<Record<TourId, TourState>>;

export type EstablishmentOnboarding = {
  checklistCollapsed?: boolean;
  checklistCompletedAt?: Date | null;
  completedAt?: Date | null;
  completedVia?: OnboardingCompletedVia;
  snoozedAt?: Date | null;
  testOrderCompletedAt?: Date | null;
  billingDataConfirmedAt?: Date | null;
  nextStepDoneOn?: Partial<Record<string, string>>;
  nextStepSnoozedOn?: string | null;
  tourUsers?: Record<string, TourStates>;
};

export type OnboardingDraft = {
  establishmentId: string;
  currentStepIndex?: number;
  welcomeAcknowledged?: boolean;
  intentDescription?: string;
  intentMatches?: { rubro: string; score: number }[];
  intentIntents?: string[];
  catalogStep?: string;
  aiDescription?: string;
  aiGeneratedCatalog?: unknown;
  aiApplied?: boolean;
  catalogDeleted?: boolean;
  wizardStartedAt?: Date | null;
  created: Date;
  updated: Date;
};

export type EstablishmentCheckout = {
  establishmentId: string;
  deliveryCost?: number;
  paymentMethods: PaymentMethod[];
  /** Configuration for dynamically generated payment link methods */
  paymentLinkConfigs?: PaymentLinkConfigs;
  paymentMethodsOrder?: string[];
  customFields?: CustomField[];
  created: Date;
  updated: Date;

  shippingOptionInPlace?: ShippingOptionInPlace;
  shippingOptionTakeAway?: ShippingOptionTakeAway;
  shippingOptionDelivery?: ShippingOptionDelivery;
  shippingOptionParcel?: ShippingOptionParcel;
};

export type EstablishmentSecurity = {
  mfaEnabled: boolean;
  mfaAdditionalContacts: {
    name: string;
    phone: string;
  }[];
};

export type Announcements = {
  title?: string;
  body?: string;
};

export type StockDisplayCondition = 'always' | 'never' | 'less-than-equal';

export type CatalogConfiguration = {
  minimumPurchase: number;
  displaySku: boolean;
  skuInOrderMessage: boolean;
  stockDisplayCondition: StockDisplayCondition;
  stockDisplayConditionValue: number;
  displayProductPriceWithoutTaxes: boolean;
};

export interface EstablishmentProductsExport {
  enabled: boolean;
  sources: ProductsExportSource[];
}

export interface ProductsExportSource {
  type: ProductsExportSourceType;
  enabled: boolean;
  lastExecutionDate: Date | any;
  status: ProductsExportSourceStatus;
  error: string;
  url: string;
}

export enum ProductsExportSourceStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETE = 'complete',
  ERROR = 'error',
}

export type ProductsExportSourceType = 'instagram' | 'googleMerchant';

export interface ExportJob {
  establishmentId: string;
  establishmentSlug: string;
  type: ProductsExportSourceType;
  createdAt: Date;
}

export type Feature =
  | 'DELIVERY_ZONES'
  | 'PARCEL_SHIPPING'
  | 'PRODUCTS_EXPORT'
  | 'PRODUCT_PRESENTATIONS'
  | 'PRODUCT_OPTIONS'
  | 'PRODUCT_GROUPS'
  | 'THEME'
  | 'QR_CODE'
  | 'DIGITAL_MENU'
  | 'COUPONS'
  | 'SEO_SOCIAL_MEDIA'
  | 'SLUG_CUSTOMIZATION'
  | 'ANALYTICS'
  | 'CHECKOUT_CUSTOM_FIELDS'
  | 'WORKING_HOURS'
  | 'STATISTICS_EXPLORE_RESULTS'
  | 'IMAGE_GALLERY'
  | 'IMAGE_GALLERY_EXTENDED'
  | 'PRODUCT_ITEM_IMAGES'
  | 'WHITE_LABEL_BRANDING'
  | 'INTEGRATIONS'
  | 'INTEGRATIONS_PAYMENT_COLLECTION'
  | 'INTEGRATIONS_MANAGEMENT'
  | 'INTEGRATIONS_FUDO'
  | 'INTEGRATIONS_THINKION'
  | 'INTEGRATIONS_MRC'
  | 'INTEGRATIONS_CUCINA'
  | 'INTEGRATIONS_PUNIYA'
  | 'INTEGRATIONS_PEDIDOS_YA'
  | 'INTEGRATIONS_PEDIDOS_YA_MARKETPLACE'
  | 'INTEGRATIONS_RAPIBOY'
  | 'INTEGRATIONS_JELP'
  | 'INTEGRATIONS_CABIFY'
  | 'INTEGRATIONS_CORREO_ARGENTINO'
  | 'INTEGRATIONS_EPICK'
  | 'INTEGRATIONS_ARCA'
  | 'ALT_LANGUAGE'
  | 'STOCK_CONTROL'
  | 'INVENTORY_MANAGEMENT'
  | 'BULK_EDIT'
  | 'SKU'
  | 'CATEGORY_AVAILABILITY'
  | 'ANNOUNCEMENTS'
  | 'ADVANCED_STATISTICS'
  | 'THERMAL_PRINTING'
  | 'ADS'
  | 'PRODUCT_TAGS'
  | 'DYNAMIC_CATEGORIES'
  | 'USER_MANAGEMENT'
  | 'ORDER_EDITOR'
  | 'PROMOTIONS'
  | 'CASH_REGISTER'
  | 'SECURITY'
  | 'SUPPLIES_STOCK'
  | 'WHATSAPP_BOT'
  | 'TABLES'
  | 'PURCHASE_RESTRICTIONS';

export type FeatureTrial = {
  feature: Feature;
  trialEndDate?: Date;
};

export enum DeliveryType {
  FIXED = 'fixed',
  DELIVERY_ZONES = 'delivery-zones',
  TO_BE_DEFINED = 'to-be-defined',
  PROVIDERS = 'providers',
}

export type DeliveryOperation = 'OWN' | 'INTEGRATION';
export type QuotationMethod = 'MANUAL' | 'INTEGRATION';
export type DeliveryZoneType = 'circle' | 'polygon';
export type DeliveryZoneBase<T extends DeliveryZoneType> = {
  type: T;
  name?: string;
  cost?: number;
  minimumPurchase?: number;
  minimumPurchaseForFreeShipping?: number;
  color: string;
};
export type DeliveryZoneCircle = DeliveryZoneBase<'circle'> & {
  center: { lat: number; lng: number };
  radius: number;
};
export type DeliveryZonePolygon = DeliveryZoneBase<'polygon'> & {
  paths: { lat: number; lng: number }[];
};

export type DeliveryZone = DeliveryZoneCircle | DeliveryZonePolygon;
export type CustomFieldType =
  | 'text'
  | 'digits'
  | 'number'
  | 'date'
  | 'single-choice'
  | 'multiple-choice';
export type CustomFieldOption = { value: string };

export const RESTRICTION_ALL = 'ALL' as const;

export function isRestrictionAll(restriction: readonly string[] | undefined): boolean {
  return !restriction || restriction.length === 0 || restriction.includes(RESTRICTION_ALL);
}

// PaymentMethodType identifies built-in methods; custom methods (type: null) are identified by their name (string)
export type PaymentMethodRestriction =
  | PaymentMethodType
  | (string & Record<never, never>)
  | typeof RESTRICTION_ALL;
export type ShippingOptionRestriction = TypesOfShippings | typeof RESTRICTION_ALL;

export interface CustomField {
  fieldType: CustomFieldType;
  name: string;
  legend?: string;
  required?: boolean;
  min?: number;
  max?: number;
  defaultValue?: number | string;
  options?: CustomFieldOption[];
  allowedPaymentMethods?: PaymentMethodRestriction[];
  allowedShippingOptions?: ShippingOptionRestriction[];
}

export interface IndexedCustomField {
  field: CustomField;
  originalIndex: number;
}

export function hasCustomFieldPaymentRestriction(field: CustomField): boolean {
  return !isRestrictionAll(field.allowedPaymentMethods);
}

export function hasCustomFieldShippingRestriction(field: CustomField): boolean {
  return !isRestrictionAll(field.allowedShippingOptions);
}

export function isGlobalCustomField(field: CustomField): boolean {
  return !hasCustomFieldPaymentRestriction(field) && !hasCustomFieldShippingRestriction(field);
}

export function isCustomFieldVisible(
  field: CustomField,
  shippingOptionType: TypesOfShippings | null | undefined,
  paymentMethodValue: string | null | undefined,
): boolean {
  const shippingMatch =
    !hasCustomFieldShippingRestriction(field) ||
    (!!shippingOptionType && field.allowedShippingOptions!.includes(shippingOptionType));

  const paymentMatch =
    !hasCustomFieldPaymentRestriction(field) ||
    (!!paymentMethodValue && field.allowedPaymentMethods!.includes(paymentMethodValue));

  return shippingMatch && paymentMatch;
}

export interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
  toDate: () => Date;
}

export interface CustomFieldDetail {
  field: CustomField;
  value?: string | number | Date | FirestoreTimestamp | string[];
}

export interface EstablishmentSeoSocialMedia {
  shortDescription?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  shortImage?: string;
  favicon?: string;
  displayShareButton?: boolean;
  preventIndexing?: boolean;
  // twitterUser?: string;
}

export type PaymentGateway = 'STRIPE' | 'BOXFUL' | 'OFFLINE' | 'ZENRISE';

export interface SubscriptionInfo {
  id: string;
  plan: string; // TODO: this should be typed PlayType
  createdAt: Date;
  updatedAt: Date;
  canceledAt?: Date | null;
  canceledByUser?: boolean;
  currentPeriodStartDate: Date | null;
  currentPeriodEndDate: Date | null;
  trialPeriodStartDate?: Date | null;
  trialPeriodEndDate?: Date | null;
  paymentGateway: PaymentGateway;
  isTrial: boolean;
  requiresPayment: boolean;
  lastPaymentError?: {
    message: string;
    code: string;
    /**
     * Bank error code
     */
    declineCode?: string;
  };
  status: SubscriptionStatus;
  customerId: string;
  countryCode?: Country;
  /**
   * subscriptionIds de Zenrise que tuvo este establishment antes del actual.
   * Una reactivación con nueva suscripción crea un id nuevo y pisa `id`; el
   * viejo se preserva acá para no perder la trazabilidad (el establishmentId es
   * el identificador estable; el subscriptionId es volátil).
   */
  previousSubscriptionIds?: string[];
  lastUpgrade?: Date;
  billingType?: BillingType;
  promotionCode?: string;
  requiresOnboarding?: boolean;
  establishmentDeletionType?: 'IMMEDIATELY' | 'END_OF_PERIOD';
  debtPaidAt?: Date | null;
  /**
   * Estado del cobro de deuda durante una reactivación. Da idempotencia: guarda
   * el `invoiceId` de la última factura de reactivación generada para que un
   * reintento verifique su cobro en Zenrise en vez de generar otra. `lockedAt`
   * marca un cobro en curso y frena reintentos casi simultáneos; se limpia
   * (campo en `null`) al liberar el lock o al reactivar con éxito.
   */
  debtCharge?: {
    invoiceId?: string | null;
    lockedAt?: Date | null;
  } | null;
  planChangeLockedAt?: Date | null;
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DELETED = 'deleted',
}

export const GRACE_PERIOD_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

export type SubscriptionCheckFields = Pick<
  SubscriptionInfo,
  'status' | 'isTrial' | 'currentPeriodEndDate' | 'trialPeriodEndDate'
>;

export type SubscriptionAccessState = 'active' | 'grace-period' | 'expired';

export type GracePeriodFields = {
  subscription?: SubscriptionCheckFields | null;
  gracePeriodDays?: number | null;
};

function getEndOfCurrentPeriodTime(
  subscription: Omit<SubscriptionCheckFields, 'status'>,
): number | null {
  const endOfCurrentPeriod = subscription.isTrial
    ? subscription.trialPeriodEndDate
    : subscription.currentPeriodEndDate;

  if (typeof endOfCurrentPeriod === 'string') {
    return new Date(endOfCurrentPeriod).getTime();
  }
  if (endOfCurrentPeriod instanceof Date) {
    return endOfCurrentPeriod.getTime();
  }
  return null;
}

function toGracePeriodEndTime(
  endOfCurrentPeriodTime: number,
  gracePeriodDays: number | null | undefined,
): number {
  return endOfCurrentPeriodTime + (gracePeriodDays ?? GRACE_PERIOD_DAYS) * DAY_MS;
}

export function getGracePeriodEnd({
  subscription,
  gracePeriodDays,
}: GracePeriodFields): Date | null {
  const endOfCurrentPeriodTime = subscription ? getEndOfCurrentPeriodTime(subscription) : null;

  if (!endOfCurrentPeriodTime) {
    return null;
  }

  return new Date(toGracePeriodEndTime(endOfCurrentPeriodTime, gracePeriodDays));
}

export function getSubscriptionAccessState({
  subscription,
  gracePeriodDays,
}: GracePeriodFields): SubscriptionAccessState {
  if (!subscription || subscription.status === SubscriptionStatus.ACTIVE) {
    return 'active';
  }

  const endOfCurrentPeriodTime = getEndOfCurrentPeriodTime(subscription);

  if (!endOfCurrentPeriodTime) {
    return 'expired';
  }

  const now = Date.now();

  if (now <= endOfCurrentPeriodTime) {
    return 'active';
  }

  if (
    subscription.status === SubscriptionStatus.INACTIVE &&
    now <= toGracePeriodEndTime(endOfCurrentPeriodTime, gracePeriodDays)
  ) {
    return 'grace-period';
  }

  return 'expired';
}

export function isSubscriptionActive(
  subscription: SubscriptionCheckFields | null | undefined,
): boolean {
  return getSubscriptionAccessState({ subscription }) === 'active';
}

export function isSubscriptionInGracePeriod(establishment: GracePeriodFields): boolean {
  return getSubscriptionAccessState(establishment) === 'grace-period';
}

export interface ContactInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  discoverySource?: string;
  dni?: string;
  documentType?: 'DNI' | 'CUIT' | 'CUIL';
}

export type PaymentMethodPriceModifier = 'none' | 'extraCharge' | 'discount';

// ============================================================
// PaymentLink Dynamic Generation Types
// ============================================================

export type PaymentLinkProvider = 'mercadopago' | 'uala' | 'zenrise' | 'dinelco';

export type InstallmentOption = {
  installments: number;
  surchargePercent: number;
};

export function calculateInstallmentSurcharge(
  baseAmount: number,
  installmentOptions: InstallmentOption[] | undefined,
  selectedInstallments: number | undefined,
): { installmentSurcharge: number; installmentSurchargePercent: number } {
  if (!selectedInstallments || !installmentOptions?.length) {
    return { installmentSurcharge: 0, installmentSurchargePercent: 0 };
  }

  const option = installmentOptions.find(o => o.installments === selectedInstallments);
  if (!option?.surchargePercent) {
    return { installmentSurcharge: 0, installmentSurchargePercent: 0 };
  }

  return {
    installmentSurcharge: Math.round(((baseAmount * option.surchargePercent) / 100) * 100) / 100,
    installmentSurchargePercent: option.surchargePercent,
  };
}

export type PaymentLinkConfig = {
  name?: string;
  priceModifier: PaymentMethodPriceModifier;
  extraCharge?: number;
  discount?: number;
  legend?: string;
  installmentOptions?: InstallmentOption[];
};

export type PaymentLinkConfigs = Partial<Record<PaymentLinkProvider, PaymentLinkConfig>>;

// Move enum before types that depend on it
export enum PaymentMethodType {
  CASH = 'CASH',
  MERCADOPAGO = 'MERCADOPAGO',
  CARD = 'CARD',
  UALA_LINK = 'UALA_LINK',
  MERCADOPAGO_LINK = 'MERCADOPAGO_LINK',
  ZENRISE_LINK = 'ZENRISE_LINK',
  DINELCO_LINK = 'DINELCO_LINK',
  TRANSFER = 'TRANSFER',
  PEDIDOSYA = 'PEDIDOSYA',
  OTHER = 'OTHER',
}

// ============================================================
// PaymentMethod Discriminated Unions
// ============================================================

/** Base type with common fields for all payment methods */
export type PaymentMethodBase<T extends PaymentMethodType> = {
  type: T;
  removable: boolean;
  name: string;
  priceModifier: PaymentMethodPriceModifier;
  extraCharge?: number;
  discount?: number;
  active: boolean;
  legend?: string;
  countryAvailables?: Country[];
  installmentOptions?: InstallmentOption[];
};

/** Cash payment method */
export type PaymentMethodCash = PaymentMethodBase<PaymentMethodType.CASH> & {
  displayCashPaymentAmount?: boolean;
};

/** Card payment method */
export type PaymentMethodCard = PaymentMethodBase<PaymentMethodType.CARD>;

/** MercadoPago (direct integration) payment method */
export type PaymentMethodMercadoPago = PaymentMethodBase<PaymentMethodType.MERCADOPAGO>;

/** MercadoPago Link payment method */
export type PaymentMethodMercadoPagoLink = PaymentMethodBase<PaymentMethodType.MERCADOPAGO_LINK>;

/** Uala Link payment method */
export type PaymentMethodUalaLink = PaymentMethodBase<PaymentMethodType.UALA_LINK>;

/** Zenrise Link payment method */
export type PaymentMethodZenriseLink = PaymentMethodBase<PaymentMethodType.ZENRISE_LINK>;

/** Dinelco Link payment method */
export type PaymentMethodDinelcoLink = PaymentMethodBase<PaymentMethodType.DINELCO_LINK>;

/** Transfer details for bank transfers */
export type TransferDetails = {
  cvu: string;
  alias: string;
  accountHolder: string;
};

/** Bank transfer payment method */
export type PaymentMethodTransfer = PaymentMethodBase<PaymentMethodType.TRANSFER> & {
  /** Bank transfer details for manual transfers (CVU/Alias/Holder) */
  transferDetails?: TransferDetails;
};

/** Union type of all payment methods */
export type PaymentMethodOther = PaymentMethodBase<PaymentMethodType.OTHER>;

export type PaymentMethod =
  | PaymentMethodCash
  | PaymentMethodCard
  | PaymentMethodMercadoPago
  | PaymentMethodMercadoPagoLink
  | PaymentMethodUalaLink
  | PaymentMethodZenriseLink
  | PaymentMethodDinelcoLink
  | PaymentMethodTransfer
  | PaymentMethodOther;

export type CheckoutPaymentMethodType = PaymentMethod['type'];

export interface EstablishmentTheme {
  color1?: string;
  colorContrast1?: string;
  color2?: string;
  colorContrast2?: string;
  color3?: string;
  color4?: string;
}

export interface WorkingDays {
  isActive: boolean;
  enableOrdersOnClosed: boolean;
  selectedDaysOfWeek: (keyof WorkingDays['daysOfWeekConfig'])[];
  customizeHoursByDay: boolean;
  primaryWorkingHours?: TimeRange;
  secondaryWorkingHours?: TimeRange;
  daysOfWeekConfig: {
    1: WorkingHours;
    2: WorkingHours;
    3: WorkingHours;
    4: WorkingHours;
    5: WorkingHours;
    6: WorkingHours;
    7: WorkingHours;
  };
}

export type TemporaryClosure = {
  mode: 'now' | 'schedule' | 'disabled';
  from?: Date;
  to?: Date;
  message?: string;
  allowCatalogBrowsing?: boolean;
};

export type SuppliesConfiguration = {
  targetMarginPct: number;
};

export type ActiveInventoryCount = {
  countId: string;
  blockOrders: boolean;
  startedAt: Date;
  scope: 'full' | 'partial';
  scopeCategoryIds?: string[];
  includesOptionItems?: boolean;
};

export interface WorkingHours {
  primaryWorkingHours: TimeRange;
  secondaryWorkingHours: TimeRange;
}

export interface TimeRange {
  from?: number;
  to?: number;
}

export type ProductGroupingStrategy = 'category' | 'product-group' | 'none';

export type Category = WithAltLanguages<
  {
    id: string;
    added: Date;
    updated: Date;
    establishmentId: string;
    image?: string;
    name: string;
    active: boolean;
    sortOrder: number;
    hideLabelInList?: boolean;
    availability: {
      isActive: boolean;
      isHidden: boolean;
      daysOfWeek: number[];
      timeRange: TimeRange;
    };
    isDynamic: boolean;
    dynamicGrouping?: ProductGroupingStrategy;
    linkedProductTagIds?: string[];
    productsOrder?: string[];
    imageToClone?: ImageToClone;
    clonedFrom?: string;
    createdByOnboarding?: boolean;
  },
  'name'
>;

export type Product = WithAltLanguages<
  {
    id: string;
    added: Date;
    updated: Date;
    categoryId: string;
    establishmentId?: string;
    description?: string;
    name: string;
    sku?: string;
    price: number;
    priceDiscount?: number;
    cost?: number | null;
    lastPurchasePrice?: number;
    targetMarginPct?: number | null;
    sortOrder: number;
    aliquot?: AliquotAmount;
    hidden?: boolean;
    presentations?: OptionConfiguration | null;
    options?: OptionConfiguration[];
    linkedOptionIds?: string[]; // This is the list of Shared Options linked with this Product
    brand?: string;
    link?: string;
    categoryName?: string;
    groupName?: string;
    images: string[];
    linkedProductTagIds: string[];
    linkedSupplies?: Record<string, number>;
    primaryTagId?: string;
    imagesToClone?: ImageToClone[];
    clonedFrom?: string;
    createdByOnboarding?: boolean;
    isOptionItem?: boolean;
    weight?: number;
    height?: number;
    length?: number;
    width?: number;
    allowedShippingOptions?: ShippingOptionRestriction[];
    allowedPaymentMethods?: PaymentMethodRestriction[];
  },
  'name' | 'description' | 'groupName'
>;

export type ImageToClone = {
  srcPath: string;
  destPath: string;
};

export type ProductListItemDTO = Product & {
  searchableName: string;
  listIndex: number;
  isUpdatingImages: boolean;
};

export interface SharedOption {
  id?: string;
  establishmentId: string;
  linkedItemIds: string[];
  sortOrder: number;
  details: OptionConfiguration;
  clonedFrom?: string;
  createdByOnboarding?: boolean;
}

export type SharedOptionWithItems = {
  sharedOption: SharedOption;
  linkedItems: Product[];
};

export type OptionConfigurationType = 'customization' | 'extras';

export type OptionConfiguration = WithAltLanguages<
  {
    id: string;
    name: string;
    type?: OptionConfigurationType;
    rangeType: ProductOptionRangeType;
    min: number;
    max?: number;
    displayItemQuantities?: boolean;
    items?: ProductOptionItem[];
    sortOrder?: number;
    created?: Date;
    updated?: Date;
    clonedFrom?: string;
  },
  'name'
>;

export enum ProductOptionRangeType {
  ANY = 'any',
  FIXED = 'fixed',
  RANGE = 'range',
}

export type AliquotAmount = number | 'exempt' | null;
export type ProductOptionItem = WithAltLanguages<
  {
    id: string;
    name: string;
    sku?: string;
    hidden?: boolean;
    linkedSupplies?: Record<string, number>;
    isOptionItem?: boolean;
    image?: string;
    price: number;
    priceDiscount?: number;
    cost?: number | null;
    lastPurchasePrice?: number;
    targetMarginPct?: number | null;
    maxSelection?: number;
    quantity?: number;
    externalId?: string;
    description?: string;
    aliquot?: AliquotAmount;
    sortOrder?: number;
    weight?: number;
    height?: number;
    length?: number;
    width?: number;
    type?: ShippingItemType;
    clonedFrom?: string;
  },
  'name' | 'description'
>;

export type CartItemCategory = Pick<
  Category,
  'id' | 'establishmentId' | 'image' | 'name' | 'active' | 'availability' | 'clonedFrom'
>;
export type CartItemProduct = Pick<
  Product,
  | 'id'
  | 'categoryId'
  | 'establishmentId'
  | 'description'
  | 'name'
  | 'sku'
  | 'price'
  | 'priceDiscount'
  | 'aliquot'
  | 'groupName'
  | 'images'
  | 'linkedProductTagIds'
  | 'weight'
  | 'height'
  | 'length'
  | 'width'
  | 'presentations' // NOTE: this shouldn't be necessary but is needed by some dependency during purchase process
  | 'clonedFrom'
  | 'hidden'
  | 'allowedShippingOptions'
  | 'allowedPaymentMethods'
>;

export interface CartItem {
  id: string;
  product: CartItemProduct;
  category: CartItemCategory;
  selectedPresentation?: CartItemOptionItems;
  selectedOptions?: CartItemOptionItems[];
  observations: string;
  qty: number;
  externalId?: string;
}

export interface CartItemOptionItems {
  option: OptionConfiguration;
  items: ProductOptionItem[];
}

export type TransferPayer = {
  document: string;
  taxId: string | null;
  name: string | null;
};

export type TransferPayerStatus = 'verified' | 'unverified' | 'provided';

export type ResolvedTransferPayer = TransferPayer & { status: TransferPayerStatus };

export interface PersonalInformation {
  name?: string | null;
  address?: Address | null;
  phone?: string | null;
  email?: string | null;
  transferPayer?: TransferPayer | null;
}

export interface CustomAddressComponents {
  freeSearch?: string;
}

export interface Address {
  street?: string | null;
  addressDetails?: AddressDetails | null;
  placeType?: AddressPlaceType | null;
  placeName?: string | null;
  lot?: string | null;
  floorAndNumber?: string | null;
  references?: string | null;
}

export type AddressPlaceType = 'house' | 'building' | 'business' | 'housing-state' | 'other';

export type LatLng = { lat: number; lng: number };

export interface AddressDetails {
  freeSearch?: string;
  placeId?: string;
  street: string;
  streetNumber: string;
  neighborhood?: string;
  state?: string;
  locality?: string;
  country?: string;
  postalCode?: string;
  fullAddress: string;
  coordinates?: LatLng;
  useCustomLocation?: boolean;
  url?: string;
  timezone?: string;
  suggestedPlaceType?: AddressPlaceType;
  suggestedPlaceName?: string;
  resultType?:
    | 'locality'
    | 'administrativeArea'
    | 'houseNumber'
    | 'intersection'
    | 'street'
    | 'place';
}

export type ShippingOptionBase<T extends TypesOfShippings> = {
  active: boolean;
  name: string; // NOTE: probably this should be removed, but unkown implications
  type: T;
  allowedPaymentMethods: string[] | ['ALL'];
};

export type ShippingOptionTakeAway = ShippingOptionBase<TypesOfShippings.TAKE_AWAY>;
export type ShippingOptionInPlace = ShippingOptionBase<TypesOfShippings.IN_PLACE> & {
  tableService?: boolean;
};
export type ShippingOptionDelivery = ShippingOptionBase<TypesOfShippings.DELIVERY> & {
  deliveryOperation?: DeliveryOperation;
  deliveryType?: DeliveryType;
  minimumPurchase?: number;
  minimumPurchaseForFreeShipping?: number;
  deliveryCost?: number;
  deliveryZones?: DeliveryZone[];
  outOfDeliveryZoneShippingAllowed?: boolean;
  disableAddressInMap?: boolean;
};
export type ShippingOptionParcel = ShippingOptionBase<TypesOfShippings.PARCEL> & {
  quotationMethod: QuotationMethod;
  minimumPurchase?: number;
  minimumPurchaseForFreeShipping?: number;
  disableAddressInMap?: boolean;
};
export type ShippingOption =
  | ShippingOptionTakeAway
  | ShippingOptionInPlace
  | ShippingOptionDelivery
  | ShippingOptionParcel;

export type EndOrderDetailPaymentStatus =
  | 'PENDING'
  | 'PARTIAL'
  | 'PAID'
  | 'REFUNDED'
  | 'REJECTED'
  | 'CANCELLED';

/**
 * @deprecated Use EndOrderDetailDigitalPayment instead. Kept for backward compatibility.
 */
export type EndOrderDetailPaymentLink = {
  status: EndOrderDetailPaymentStatus;
  id?: string;
  url?: string;
  updated?: Date;
};

/**
 * Digital payment information for both payment links and bank transfers.
 * Supports multiple payment types with a discriminator field.
 */
export type EndOrderDetailDigitalPayment = {
  /** Payment type discriminator */
  type: 'link' | 'transfer';

  /** Payment status */
  status: EndOrderDetailPaymentStatus;

  /** Payment provider identifier */
  provider?: 'chytapay' | 'cucuru' | 'mercadopago' | 'zenrise' | 'uala' | 'dinelco';

  providerReferenceId?: string;
  providerPaymentId?: string;

  /** Last update timestamp */
  updated?: Date;

  // Link-specific fields (backward compatible)
  /** Payment link ID (for link type) */
  id?: string;

  /** Payment link URL (for link type) */
  url?: string;

  amount?: number;

  // Transfer-specific fields
  /** CVU (Clave Virtual Uniforme) for bank transfers */
  cvu?: string;

  /** Account holder name for transfers */
  accountHolder?: string;

  customerAccount?: string;

  /** Account alias (optional, only for Cucuru in Argentina) */
  alias?: string;

  /** Additional info (e.g., for partial payments: remaining amount) */
  additionalInfo?: string;

  /** Accumulated amount paid (for partial payment tracking) */
  paidAmount?: number;

  installmentCount?: number;
  installmentSurchargePercent?: number;

  refundError?: string;
  refundAttemptedAt?: Date;

  conciliationType?: DigitalPaymentConciliationType;
  reconciledPayer?: ReconciledPayer | null;
  unmatchedPayment?: UnmatchedTransferNotice | null;
};

export type DigitalPaymentConciliationType = 'cvu' | 'cuil';

export type ReconciledPayer = {
  taxId: string | null;
  name: string | null;
  account: string | null;
};

export type UnmatchedTransferOutcome =
  | 'imputation_requested'
  | 'imputation_rejected'
  | 'not_imputable';

export type UnmatchedTransferNotice = {
  amount: number;
  payerTaxId: string | null;
  payerName: string | null;
  reason: string;
  outcome: UnmatchedTransferOutcome;
  rejectionReason: string | null;
  receivedAt: Date;
};

export const marketplaceSources = ['pedidosya'] as const;
export type MarketplaceSource = (typeof marketplaceSources)[number];

export const endOrderSources = ['app', 'admin', ...marketplaceSources] as const;
export type EndOrderSource = (typeof endOrderSources)[number];

export const marketplaceSourceFeatures: Record<MarketplaceSource, Feature> = {
  pedidosya: 'INTEGRATIONS_PEDIDOS_YA_MARKETPLACE',
};

export function isMarketplaceSource(source: EndOrderSource): source is MarketplaceSource {
  return marketplaceSources.some(marketplaceSource => marketplaceSource === source);
}

export const marketplaceProviders = ['pedidosya-marketplace'] as const;
export type MarketplaceProvider = (typeof marketplaceProviders)[number];
export const marketplaceOrderReferencePrefixes: Record<MarketplaceProvider, string> = {
  'pedidosya-marketplace': 'PY',
};

export function formatMarketplaceOrderReference(
  provider: MarketplaceProvider,
  externalOrderId: string,
): string {
  return `${marketplaceOrderReferencePrefixes[provider]}-${externalOrderId}`;
}
export type MarketplaceExpeditionType = 'marketplace-delivery' | 'vendor-delivery' | 'pickup';
export type MarketplaceCollector = 'marketplace' | 'establishment';
export type MarketplaceReviewReason = 'unlinked-items' | 'missing-stock' | 'amount-mismatch';
export type MarketplaceTotalCheck = { reportedTotal: number; calculatedTotal: number };
export const marketplaceRejectReasons = [
  'TOO_BUSY',
  'CLOSED',
  'ITEM_UNAVAILABLE',
  'MENU_ACCOUNT_SETTINGS',
  'NO_RESPONSE',
] as const;
export type MarketplaceRejectReason = (typeof marketplaceRejectReasons)[number];

export type MarketplaceOrder = {
  provider: MarketplaceProvider;
  externalOrderId: string;
  shortCode: string;
  expeditionType: MarketplaceExpeditionType;
  pickupAt: Date | null;
  acceptBefore: Date | null;
  preparationRange: { minPickupAt: Date; maxPickupAt: Date; intervalsInMinutes: number[] } | null;
  collectedBy: MarketplaceCollector;
  platformDiscount?: number;
  totalCheck?: MarketplaceTotalCheck;
  unlinkedItemsCount: number;
  missingStockLineIds?: string[];
  reviewReasons: MarketplaceReviewReason[];
  riderWaiting: boolean;
  riderArrivedAt: Date | null;
  preparationAdjustment: { pickupAt: Date; requestedBy: string | null } | null;
  rejectReason: MarketplaceRejectReason | null;
  canceledBy: MarketplaceCollector | null;
  notified: {
    accepted: boolean;
    rejected: boolean;
    prepared: boolean;
    pickedUp: boolean;
    preparationAdjustmentAt: Date | null;
  };
};

export type EndOrderTable = {
  id: string;
  name: string;
  sectorId?: string;
  sectorName?: string;
  guests?: number;
  waiterName?: string;
  openedAt?: Date;
  closedAt?: Date;
};

// a.k.a ordersnapshot
export type EndOrderDetails = {
  id?: string;
  sequenceId: number;
  status: OrderStatus;
  created: Date;
  updated: Date;
  establishmentId: string;
  personalInformation: PersonalInformation;
  /**
   * Número real de WhatsApp desde el que el cliente envió el pedido (reconciliado desde la
   * conversación de Kapso). Se prefiere sobre personalInformation.phone para notificaciones,
   * porque el teléfono del checkout puede ser distinto al WhatsApp con el que chatea.
   */
  whatsappPhone?: string | null;
  billingInformation?: BillingInformation | null;
  taxVoucherSummary?: TaxVoucherSummary | null;
  cartItems: CartItem[];
  totalAmount: number;
  totalAmountWithDiscount: number;
  totalProductDiscount: number;
  promotionDiscount?: number;
  deliveryCost: number;
  couponDiscount?: number;
  paymentMethodExtraCharge?: number;
  paymentMethodDiscount?: number;
  installmentSurcharge?: number;
  finalAmount?: number;
  couponConfigurationSnapshot?: CouponConfigurationResponse;
  appliedPromotionsSnapshot?: AppliedPromotion[];
  // @deprecated - payment link is generated dynamically on the BE
  customFieldDetails?: CustomFieldDetail[];
  outOfDeliveryZone?: boolean;
  paymentMethod?: PaymentMethod | null;
  shippingOption?: ShippingOption | null | undefined;
  tableNumber?: string | null | undefined;
  table?: EndOrderTable;
  tableSessionId?: string;
  howMuch?: number | null | undefined;
  userUid?: string;
  cancelReason?: OrderCancelReason;
  cancelReasonDetails?: string;
  restoreSupplies?: boolean;
  inProgressDate?: Date;
  readyDate?: Date;
  shippedDate?: Date;
  completedDate?: Date;
  canceledDate?: Date;
  internalNotes?: string;
  tags: string[];
  currencyCode: CurrencyCode;
  retries?: number;

  /**
   * @deprecated Use digitalPayment instead. Kept for backward compatibility.
   */
  paymentLink?: EndOrderDetailPaymentLink | null;

  /**
   * Digital payment information (payment links or bank transfers)
   */
  digitalPayment?: EndOrderDetailDigitalPayment | null;

  paidAmount?: number;
  payments?: OrderPayment[];

  // Food Bank DOnation
  foodBankDonationAmount?: number;
  // Parcel tracking email
  parcelTrackingEmail?: string | null;
  /*
    5: adds sequenceId
    7: adds status, cancelReason, cancelReasonDetails, tags, internalNotes
  */
  schemaVersion: number;
  shippingEstimate?: ShippingEstimate;
  shippingOrder?: ShippingOrder;
  shippingAutoSendError?: string;
  shippingAutoSendAttemptedAt?: Date;
  source: EndOrderSource;
  marketplaceOrder?: MarketplaceOrder;
  copiedFrom?: string;
};

export interface BillingInformation {
  provider: string;
  vatCondition: string | number;
  name?: string | null;
  documentType?: number;
  documentNumber?: number | null;
  address?: string | null;
  email?: string | null;
}

export type TaxVoucherOperation = 'bill' | 'credit' | 'pending';
export type TaxVoucherPrintCopies = 'single' | 'double' | 'triple';
export type TaxVoucherOptions = {
  groupingConceptName?: string;
  groupByOrder?: boolean;
  printCopies?: TaxVoucherPrintCopies;
};
export interface TaxVoucher {
  id: string;
  establishmentId: string;
  provider: string;
  details: {
    operation: TaxVoucherOperation;
    type: string | number;
    orderIds: string[];
    orderSequenceIds: number[];
    voucherRef: string;
    lineItemsByOrder: { orderId: string; lineItems: any[] }[];
    buyerBillingInfo: BillingInformation;
    sellerInfoExtra: {
      logoUrl: string;
      fantasyName: string;
    };
    accountDetails: {
      autoSendInvoice: boolean;
      supportEmail: string;
    };
    defaultAliquot?: AliquotAmount;
    taxVoucherOptions?: TaxVoucherOptions;
    observations?: string;
    voucherTotals: TaxVoucherTotals;
    currency: CurrencyCode;
    exchangeRate: number;
    associatedTaxVoucherSummary?: TaxVoucherSummary | null;
    created: Date;
    updated: Date;
  };
}
export interface TaxVoucherEmailSender {
  taxVoucherId: string;
  establishmentId: string;
  /**
   * Firestore collection holding the referenced voucher. Defaults to 'taxVouchers'
   * (client billing); Pedix's own emission sets it to 'pedixTaxVouchers'.
   */
  sourceCollection?: string;
  error?: string;
  details: {
    email: string;
    templateParams: {
      establishmentName: string;
      supportEmail: string;
    };
    /**
     * Vouchers adicionales a adjuntar en el MISMO email (ej: la nota de crédito junto a la
     * factura de reemplazo en un rebill). Cada id se resuelve en sourceCollection.
     */
    attachVoucherIds?: string[];
    created: Date;
    ttlDate: Date;
  };
}
export type TaxVoucherCreationParams = {
  establishmentId: string;
  orderIds: string[];
  billingInformation: BillingInformation;
  groupingConceptName?: string;
  groupByOrder?: boolean;
};

export type TaxVoucherTotals = {
  exemptAmount: number; // TODO: remove this field once voucherTotals is used
  vatAmount: number; // TODO: remove this field once voucherTotals is used
  tributesAmount: number; // TODO: remove this field once voucherTotals is used
  netAmount: number; // TODO: remove this field once voucherTotals is used
  totalAmount: number; // TODO: remove this field once voucherTotals is used
  vatDetails?: {
    // TODO: remove this field once voucherTotals is used
    id: number;
    aliquot: number;
    baseAmount: number;
    vatAmount: number;
  }[];
};

export type TaxVoucherSummary = {
  id: TaxVoucher['id'];
  establishmentId: TaxVoucher['establishmentId'];
  provider: TaxVoucher['provider'];
  associatedTaxVoucherId?: TaxVoucher['id'];
  associatedTaxVoucherRef?: TaxVoucher['details']['voucherRef'];
  details: Pick<
    TaxVoucher['details'],
    'operation' | 'type' | 'orderIds' | 'voucherRef' | 'voucherTotals' | 'currency' | 'created'
  >;
};

export interface Order {
  id?: string;
  sequenceId: number;
  status: OrderStatus;
  establishmentId: string;
  totalAmount: number;
  currencyCode: CurrencyCode;
  phone: number | null;
  date: Date;
  updated: Date; // NOTE: this was added after ~2024-03-15, so this field is null in Orders prior to this change
  address: string;
  paymentMethod: string;
  shippingOption?: string;
  shippingType?: TypesOfShippings | null;
  name: string;
  schemaVersion: number;
  addressDetails: AddressDetails;
  tags?: string[];
  internalNotes?: string;
  cancelReason?: OrderCancelReason;
  cancelReasonDetails?: string;

  /**
   * @deprecated Use digitalPayment instead. Kept for backward compatibility.
   */
  paymentLink?: EndOrderDetailPaymentLink;

  /**
   * Digital payment information (payment links or bank transfers)
   */
  digitalPayment?: EndOrderDetailDigitalPayment | null;

  paidAmount?: number;
  payments?: OrderPayment[];

  shippingEstimate?: ShippingEstimate;
  shippingOrder?: ShippingOrder;
  shippingAutoSendError?: string;
  shippingAutoSendAttemptedAt?: Date;
  source: EndOrderSource;
  marketplaceOrder?: MarketplaceOrder;
  userUid?: string;
  taxVoucherSummary?: TaxVoucherSummary | null;
  table?: EndOrderTable;
  tableSessionId?: string;
}

export type OrderItemLineItem = {
  categoryName: string;
  groupName: string;
  name: string;
  quantity: number;
  unitPrice: number;
  aliquot?: AliquotAmount;
  total: number;
  sku?: string;
};

export const orderStatuses = [
  'new',
  'in-progress',
  'ready',
  'shipped',
  'completed',
  'canceled',
] as const;
export type OrderStatus = (typeof orderStatuses)[number];

export const orderCancelReasons = [
  'CUSTOMER_CANCELATION',
  'PRODUCTS_UNAVAILABLE',
  'WRONG_ADDRESS',
  'PAYMENT_ERROR',
  'DUPLICATED',
  'OTHER',
] as const;
export type OrderCancelReason = (typeof orderCancelReasons)[number];

export const OTHER_TABLE_VOID_REASON = 'other';

export const tableRoundVoidReasons = [
  'order-mistake',
  'customer-changed',
  'customer-rejected',
  'not-available',
  'damaged',
  OTHER_TABLE_VOID_REASON,
] as const;
export type TableRoundVoidReason = (typeof tableRoundVoidReasons)[number];

export const tableSessionCancelReasons = [
  'opened-by-mistake',
  'duplicated-session',
  'left-without-paying',
  'left-before-served',
  'order-rejected',
  'moved-to-takeaway',
  OTHER_TABLE_VOID_REASON,
] as const;
export type TableSessionCancelReason = (typeof tableSessionCancelReasons)[number];

export type TableVoidReason = TableRoundVoidReason | TableSessionCancelReason;

export type OrdersSequence = {
  lastValue: number;
  createdAt: Date;
  updatedAt: Date;
};

export const cashSessionStatuses = ['open', 'closed'] as const;
export type CashSessionStatus = (typeof cashSessionStatuses)[number];

export const DEFAULT_CASH_REGISTER_ID = 'default';

export const paymentTypes = [
  'sale',
  'refund',
  'expense',
  'withdrawal',
  'deposit',
  'overage',
  'shortage',
  'tip',
] as const;
export type PaymentType = (typeof paymentTypes)[number];

export const outgoingPaymentTypes: PaymentType[] = ['refund', 'expense', 'withdrawal', 'shortage'];

export const cashDiscrepancyPaymentTypes = ['overage', 'shortage'] as const;
export type CashDiscrepancyPaymentType = (typeof cashDiscrepancyPaymentTypes)[number];

export const OTHER_PAYMENT_REASON = 'other';

export const paymentReasonsByType = {
  withdrawal: ['bank-deposit', 'owner-withdrawal', OTHER_PAYMENT_REASON],
  deposit: ['change-fund', 'owner-contribution', 'debt-collection', OTHER_PAYMENT_REASON],
  expense: ['suppliers', 'services-taxes', 'salaries', 'minor-expenses', OTHER_PAYMENT_REASON],
} as const;
export type ManualPaymentType = keyof typeof paymentReasonsByType;
export const CLOSING_WITHDRAWAL_REASON = 'closing-withdrawal';
export type PaymentReason =
  | (typeof paymentReasonsByType)[ManualPaymentType][number]
  | typeof CLOSING_WITHDRAWAL_REASON;

export const paymentOrigins = ['manual', 'provider-webhook'] as const;
export type PaymentOrigin = (typeof paymentOrigins)[number];

export type PaymentMethodKey = string;

export type CashSessionMethodAmounts = Partial<Record<PaymentMethodKey, number>>;

export type Payment = {
  id: string;
  establishmentId: string;
  registerId: string;
  cashSessionId: string | null;
  orderId?: string;
  type: PaymentType;
  methodType: PaymentMethodType;
  amount: number;
  at: Date;
  details: {
    createdBy: string;
    reasonCode?: PaymentReason;
    reason?: string;
    source?: EndOrderSource;
    methodName?: string;
    orderSequenceId?: number;
    providerPaymentId?: string;
    origin?: PaymentOrigin;
    settlementId?: string;
  };
};

export type OrderPayment = Pick<Payment, 'id' | 'type' | 'methodType' | 'amount' | 'at'> & {
  details: Pick<Payment['details'], 'methodName'>;
};

export type OpenCashRegister = {
  id: string;
  establishmentId: string;
  registerId: string;
  sessionId: string;
  openedBy: string;
};

export const cashSessionNoteTypes = ['manual'] as const;
export type CashSessionNoteType = (typeof cashSessionNoteTypes)[number];

export type CashSessionNote = {
  id: string;
  sessionId: string;
  establishmentId: string;
  created: Date;
  type: CashSessionNoteType;
  details: {
    detail: string;
    createdBy: string;
    orderId?: string;
  };
};

export type MethodReconciliation = {
  autoAmount: number;
  manualAmount: number;
  verifiedManually: boolean;
};

export type CashDrawerBreakdown = {
  /** El saldo que dejó el cierre anterior, antes del ajuste de apertura. */
  openingCash: number;
  /**
   * El ajuste declarado al abrir, con su signo. Separado de los movimientos del turno: esa plata
   * se movió con la caja cerrada. Ausente en los cortes anteriores al asiento de apertura.
   */
  openingAdjustment?: number;
  /** Ventas cobradas en efectivo, netas de devoluciones en efectivo. */
  sales: number;
  /** Lo demás que entró al cajón: aportes. */
  otherIn: number;
  /** Lo que salió del cajón: gastos y retiros. Positivo. */
  otherOut: number;
  tips?: number;
};

export type PaymentsTotals = {
  totalIn: number;
  totalOut: number;
  net: number;
};

export type CashSessionLiveCounters = {
  paymentsCount: number;
  totalSold: number;
  byMethod: CashSessionMethodAmounts;
};

export type CashSessionClosing = {
  expectedByMethod: CashSessionMethodAmounts;
  differenceCash: number;
  differenceByMethod?: CashSessionMethodAmounts;
  paymentsCount: number;
  totalSold: number;
  totalsBySource?: Partial<Record<EndOrderSource, number>>;
  reconciliation?: Partial<Record<PaymentMethodKey, MethodReconciliation>>;
  cashDrawer?: CashDrawerBreakdown;
  nonCashMovements?: PaymentsTotals;
  tipsByMethod?: CashSessionMethodAmounts;
  cashLeftInDrawer?: number;
};

export type CloseCashSessionParams = {
  sessionId: string;
  countedCash: number;
  cashLeftInDrawer?: number;
  declaredByMethod?: CashSessionMethodAmounts;
  verifiedMethods: PaymentMethodKey[];
  manuallyVerifiedMethods: PaymentMethodKey[];
  closingComment?: string;
  paymentsCount: number;
};

export type CloseCashSessionResult = {
  success: true;
};

export const CASH_SESSION_PAYMENTS_CHANGED = 'payments-changed';

export type CashSessionPaymentsChangedDetails = {
  code: typeof CASH_SESSION_PAYMENTS_CHANGED;
  paymentsCount: number;
};

export type CashSession = {
  id: string;
  establishmentId: string;
  registerId: string;
  status: CashSessionStatus;
  from: Date;
  to: Date | null;
  openingCash: number;
  openingDifference?: number;
  openingBasedOnSessionId?: string | null;
  openedBy: string;
  countedCash?: number;
  liveTotals?: CashSessionLiveCounters;
  reminderSentAt?: Date;
  declaredByMethod?: CashSessionMethodAmounts;
  verifiedMethods?: PaymentMethodKey[];
  closedBy?: string;
  details?: {
    /** @deprecated El motivo vive en el asiento de apertura. Sólo se lee de cortes previos a 2026-09. */
    openingComment?: string;
    closingComment?: string;
  };
  closing?: CashSessionClosing;
  created: Date;
  updated: Date;
};

export interface DateRange {
  from: Date;
  to: Date;
}

export interface StatisticsSummary {
  numOfSales: number;
  salesAmount: number;
  averageTicketAmount: number;
  uniqueCustomers: number;
  paymentMethod: {
    name: string;
    amount: number;
  };
  shippingOption: {
    name: string;
    amount: number;
  };
}

export enum CouponDiscountType {
  FIXED = 'FIXED',
  PERCENTAGE = 'PERCENTAGE',
  FREE_SHIPPING = 'FREE_SHIPPING',
}

export interface CouponConfiguration {
  id: string;
  name: string;
  used: number;
  code: string;
  validFrom: Date;
  validTo: Date;
  active: boolean;
  discountType: CouponDiscountType;
  discount: number;
  quantity?: number;
  minAmount?: number;
  paymentMethods: string[] | ['ALL'];
  daysOfWeek: number[] | ['ALL'];
  categories: string[] | ['ALL'];
  productTags: string[];
  added: Date;
  updated: Date;
}

export type CouponValidationErrorCode =
  | 'NOT_FOUND'
  | 'INACTIVE'
  | 'PENDING'
  | 'EXPIRED'
  | 'SOLD_OUT';

export interface CouponUsageRegistry {
  couponConfigurationId: string;
  establishmentId: string;
  couponCode: string;
  details: {
    orderId: string;
    ordeAmount: number;
    discountAmount: number;
    userName: string;
    userPhone: string;
    date: Date;
  };
}

export type CouponConfigurationResponse = Pick<
  CouponConfiguration,
  | 'id'
  | 'code'
  | 'discountType'
  | 'discount'
  | 'daysOfWeek'
  | 'categories'
  | 'productTags'
  | 'paymentMethods'
  | 'minAmount'
  | 'updated'
>;

export interface PrivateConfigurationPaymentLink {
  clientId: string | null;
  secretId: string | null;
}
export interface PrivateConfiguration {
  mercadopagoToken: string | null;
  mercadopagoRefreshToken?: string | null;
  zenriseConfig: PrivateConfigurationPaymentLink | null;
  ualaConfig: UalaConfig | null;
}

export enum StatisticsType {
  NUM_OF_SALES = 'numOfSales',
  SALES_AMOUNT = 'salesAmount',
  AVERAGE_TICKET_AMOUNT = 'averageTicketAmount',
  UNIQUE_CUSTOMERS = 'uniqueCustomers',
  PAYMENT_METHODS = 'paymentMethod',
  SHIPPING_OPTIONS = 'shippingOption',
}

export type StatisticsTypes = 'basic' | 'advanced';

export type StatisticsAdvancedTypes = 'orders' | 'orderDetails' | 'googleAnalytics';

export interface FunctionResponseOK<T> {
  result: T;
}

export interface FunctionResponseError {
  error: {
    status: string;
    message: string;
    details?: any;
  };
}

export type FunctionResponse<T = any> = FunctionResponseOK<T> | FunctionResponseError;
export type FunctionResponseClient<T = any> = T | FunctionResponseError;

export interface ValidateCouponParams {
  establishmentId: string;
  couponCode: string;
}

export interface UseCouponParams {
  couponCode: string;
  establishmentId: string;
  userName: string;
  userPhone: string;
  couponDiscount: number;
  orderAmount: number;
}

export interface RegisterData {
  name: string;
  slug: string;
  username: string;
  password: string;
}

export const CURRENCY_CODES = [
  'ARS',
  'BOB',
  'BRL',
  'BSD',
  'CAD',
  'CLP',
  'COP',
  'CRC',
  'CUP',
  'DOP',
  'EUR',
  'GTQ',
  'GYD',
  'HNL',
  'JMD',
  'MXN',
  'NIO',
  'PAB',
  'PEN',
  'PYG',
  'USD',
  'UYU',
  'VES',
] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];
export type Country = 'AR' | 'CL' | 'MX' | 'PE' | 'UY' | 'CO' | 'BR' | 'ES' | 'PY' | 'XX';
export type LocaleID =
  | 'es-AR'
  | 'es-CL'
  | 'es-MX'
  | 'es-PE'
  | 'es-UY'
  | 'es-CO'
  | 'es-PY'
  | 'pt'
  | 'es'
  | 'es-419'
  | 'en-US';
export type CurrencyLocaleData = {
  currencyCode: CurrencyCode;
  currencySymbol: string;
  decimalsLength: number;
  thousandsSeparator: '.' | ',' | ' ';
  decimalSeparator: '.' | ',';
  excelNumberFormat: string;
};

export type REPORT_TYPE = 'ORDERS' | 'PRODUCTS';

export interface Customer {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface RegistrationData {
  id?: string;
  created?: Date;
  customer: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dni?: string;
    documentType?: 'DNI' | 'CUIT' | 'CUIL';
    discoverySource: string;
  };
  establishment: {
    name: string;
    slug: string;
    phone: string;
    countryCode: Country;
    countryDetail: string;
    categories: string[];
    cuit?: string;
    businessName?: string;
    ivaCondition?: string;
  };
  account: {
    username: string;
    password: string;
    email?: string;
  };
  seller?: string;
  couponId?: string;
  promotionCode?: string;
  planType?: PlanType;
  billingType: BillingType;
  acceptTermsAndConditions?: boolean;
}

export interface StripePromotionCode {
  discount: number;
  recurring_profit: number;
  couponId: string;
}

export interface StripeSubscriptionParams {
  registrationData: Partial<RegistrationData>;
  priceId: string;
  paymentMethodId: string;
  planType: PlanType;
}

export interface StripeSubscriptionResponse {
  subscription: any;
}

export interface StripeCancelSubscriptionParams {
  establishmentId: string;
  subscriptionId: string;
}

export interface StripeCancelSubscriptionResponse {
  result: boolean;
}

export interface MercadopagoLinkResponse {
  mercadopagoLink: string;
}

export interface MercadopagoLinkParams {
  establishmentId: string;
  totalAmount: string;
}

export interface BoxfulCustomer {
  id?: number;
  fname: string;
  lname: string;
  email: string;
  phone: number;
  custom_fields: BoxfulCustomFields[];
}

export interface BoxfulCustomFields {
  name: string;
  field_type: string;
  content_string: string;
}

export interface BoxfulCustomerCreateResponse {
  loginToken: string;
}

export interface BoxfulCreateCustomerParams {
  boxfulCustomer: BoxfulCustomer;
}

export interface BoxfulWebhook {
  subscription: BoxfulSubscription;
  customer: BoxfulCustomer;
}

interface BoxfulSubscription {
  object: string;
  id: number;
  customer_id: number;
  status: string;
  payment_type: string;
  created_at: string;
  updated_at: string;
  amount: number;
  currency: string;
  coupon_id?: any;
  plan_id: number;
  delivery_price_item_id: number;
  external_service: string;
  metadata?: any;
  authorized_at?: any;
  cancelled_at: string;
  cbu?: any;
  last_invoice: Lastinvoice;
  next_invoice?: any;
}

interface Lastinvoice {
  object: string;
  id: number;
  status: string;
  amount: number;
  due_date: string;
  currency: string;
}

export interface ZenriseCancelSubscription {
  establishmentId: string;
  subscriptionId: string;
}

export interface ZenriseSubscriptions {
  subscriptions: ZenriseSubscription[];
}

export type ZenrisePaymentLink = {
  amount: number;
  contact: {
    firstName: string;
  };
  description: string;
  firstDueDate: string;
  percentageAfterFirstDueDate: number;
  secondDueDate: string;
  percentageAfterSecondDueDate: number;
  sendPerEmail: boolean;
  backUrl: {
    success: string;
    failure: string;
  };
};

interface ZenriseSubscription {
  contact: ZenriseSubscriptionContact;
  externalReference: string;
  startDate: string;
  paymentToken?: ZenriseSubscriptionPaymentToken;
  bankAccountDto?: ZenriseBankAccount;
  collectType: string;
  planId?: any;
  creditCard?: number;
  metadata: {
    registrationData: RegistrationData | null;
  };
}

export interface ZenriseBank {
  id: number;
  name: string;
}

export interface ZenriseBankAccount {
  holderName: string;
  cbuNumber: string;
  holderIdNumber: string;
  bankId: number | null;
}

interface ZenriseSubscriptionPaymentToken {
  bin: string;
  token: string;
  lastFourDigits: string;
}

interface ZenriseSubscriptionContact {
  email: string;
  firstName: string;
  lastName: string;
  holderDocumentNumber: string;
  documentType?: 'DNI' | 'CUIT' | 'CUIL';
  phoneAreaCode: string;
  phoneNumber: string;
  externalReference: string;
}

export interface ZenriseWebhook {
  event: string;
  object: ZenriseWebhookObject;
}

export interface ZenriseWebhookObject {
  id: number;
  status: string;
  description?: any;
  subscriptionConfiguration: ZenriseWebhookConfiguration;
  contact: ZenriseWebhookContact;
  metadata: any;
  billingDates: ZenriseWebhookBillingDate;
}

interface ZenriseWebhookContact {
  id: number;
  externalReference: string;
}

interface ZenriseWebhookConfiguration {
  id: number;
  startDate: string;
  periods: number;
  amountAfterFirstDueDate?: any;
  amountAfterSecondDueDate?: any;
  firstDuePercentage: number;
  secondDuePercentage: number;
  endDate: string;
  lastFourDigits: string;
  collectType: string;
  chargeType: string;
  daysAfterFirstDue: number;
  daysAfterSecondDue: number;
}

interface ZenriseWebhookBillingDate {
  startBillingDate: string;
  nextBillingDate: string;
}

export interface AdminPanelOption {
  type: AdminPanelOptionType;
  icon: string;
  url: string;
  isVisible: boolean;
  label: string;
  class?: string;
}

export type AdminPanelOptionType =
  | 'catalogue'
  | 'general'
  | 'subscription'
  | 'theme'
  | 'seoSocialMedia'
  | 'workingHours'
  | 'paymentMethods'
  | 'shippingOptions'
  | 'checkout'
  | 'qrCode'
  | 'coupons'
  | 'promotions'
  | 'statistics'
  | 'orderList'
  | 'analytics'
  | 'instagramShopping'
  | 'integrations'
  | 'electronicBilling'
  | 'inventory'
  | 'clonning'
  | 'announcements'
  | 'ads'
  | 'invitedUsers'
  | 'security'
  | 'whatsappBot'
  | 'cashRegister'
  | 'supplies'
  | 'productCosts'
  | 'tables';

export type AdminPanelLinksNavigation =
  | 'subscriptionInfo'
  | 'subscriptionUpgrade'
  | 'helpCenter'
  | 'whatsappSupport';

export type AdminPanelHeaderNavigation =
  | 'settings'
  | 'previewMode'
  | 'notifications'
  | 'user'
  | 'whatsappBot';

export enum PlanType {
  BASIC = 'BASIC',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
}
export interface ProrationRequest {
  establishmentId: string;
  targetPlan: PlanType;
  prorationDate?: number;
  billingType: BillingType;
  countryCode: Country;
}

export interface UpdateSubscriptionRequest {
  establishmentId: string;
  targetPlan: PlanType;
  currentPlan: PlanType;
  customerId: string;
  billingType: BillingType;
  prorationDate?: number;
  paymentMethodId?: string;
  startNow?: boolean;
}

export interface Plan {
  description?: string;
  name?: string;
  prices?: any;
  features?: string[];
  amounts?: any;
}

export interface ProrationResponse {
  result: ProrationAmount;
}

export interface ProrationAmount {
  unusedCurrentPlanAmount: number;
  differenceToPayForNewPlan: number;
  targetPlanPrice: number;
  remainingDays?: number;
}

export interface ZenriseUpdateSubscriptionRequest {
  subscriptionId: number;
  planId: number;
  startNow: boolean;
}

export interface ZenriseProrationResponse {
  prorationAmount: number;
  transcurredDays: number;
  remainingDays: number;
  currentPrice: number;
  newPrice: number;
  type: string;
}

export type Vertical = 'GASTRONOMY';

export type BusinessCategoryGroup =
  | 'GASTRONOMY'
  | 'FRESH_PRODUCTS'
  | 'RETAIL'
  | 'HEALTH_AND_BEAUTY';

export type PlanFeature = {
  name: string;
  vertical?: Vertical;
  countryCodes?: Country[];
  planDetails: {
    [key: string]: PlanDetails;
  };
};

export type PlanDetails = {
  display: boolean;
  text?: string;
  custom?: boolean;
  config?: any;
};

export interface PasswordRecoveryRequest {
  id?: string;
  date: Date;
  establishmentSlug?: string;
  registrationEmail: string;
  dateUsed?: Date;
  isInvitedUser?: boolean;
}

export type AppRequestUrl = {
  hostname: string;
  pathname: string;
};

export interface CancelSubscriptionReason {
  values: string[];
  comment?: string;
  slug: string;
  establishmentId: string;
}

export type BillingType = 'MONTLY' | 'YEARLY';

export interface StripePromotionCode {
  discount: number;
  recurring_profit: number;
  couponId: string;
}

export interface StripePromotionCodeSuccess {
  couponId: string;
  promotionCode: string;
}

export interface LandingPromotions {
  startDate: Date;
  endDate: Date;
  promotionCode: string;
  codes: LandingPromotionsCode[];
}

export interface LandingPromotionsCode {
  billingType: BillingType;
  code: string;
  discount: number;
  quantityOfMonths: number;
}

export type SortInput = { id: string; sortOrder: number };

export type CustomDomain = {
  domain: string;
  provider: string;
};

export const LANGUAGE_QUERY = 'lang';
export const DEFAULT_LANGUAGE = 'es' as const;
export type ALT_LANGUAGES = 'en';
export type SUPPORTED_LANGUAGES = typeof DEFAULT_LANGUAGE | ALT_LANGUAGES;
export type AVAILABLE_LANGUAGES =
  | [SUPPORTED_LANGUAGES]
  | [SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGES];

export type WithAltLanguages<T, Keys extends string> = T & {
  [Prop in keyof T as Prop extends Keys ? `${Prop}_${ALT_LANGUAGES}` : never]?: T[Prop];
};

export type CloneCatalogOptions = {
  overwriteVisibility: boolean;
  overwriteStock: boolean;
  cloneIntegrationRelations?: boolean;
};

export type CloneCatalogParams = {
  originSlug: string;
  targetSlug: string;
  cloneOptions: CloneCatalogOptions;
};

export type BulkEditEntity = 'products' | 'option-items';

export type ProductImportExcelData = {
  productId: string;
  itemType: 'product' | 'presentation';
  name: string;
  sku?: string;
  image: string;
  multimediaFileUri?: string;
  categoryName: string;
  categoryId: string;
  groupName?: string;
  description?: string;
  itemId?: string;
  presentationName?: string;
  presentationDescription?: string;
  presentationSelectionQty?: number | null; // Any number equals RangeType.FIXED; null equals RangeType.ANY
  price: number;
  priceDiscount?: number | null;
  aliquot?: AliquotAmount;
  // Ausente/`undefined` = la celda vino vacía (o no hay columna Stock) → no se toca el stock.
  // `null` = literal "Sin control" en la planilla → baja de control. Número = cantidad.
  stockQty?: number | null;
  cost?: number | null;
  targetMarginPct?: number | null;
  status: 'ACTIVO' | 'INACTIVO';
  tagList: string;
  promotionList: string;
  primaryTagName?: string;
  rowNumber?: number;
  isOptionItem: boolean;
  productRef: Product;
  // Dimensiones físicas para envíos
  weight?: number;
  height?: number;
  length?: number;
  width?: number;
};

export type ProductImportExcelDataForValidation = Omit<
  ProductImportExcelData,
  'tagList' | 'promotionList' | 'multimediaFileUri'
> & {
  tagList: string[] | null;
  promotionList: string[] | null;
  multimediaFileUri: string[] | null;
};

export type ProductGridData = {
  id: string;
  name: string;
  image: string;
  multimediaFileUris?: string[];
  categoryId: string;
  categoryName: string;
  groupName?: string;
  description?: string;
  hidden: boolean;
  presentationSelectionQty?: number | null;
  productTags: ProductTag[];
  promotions: Promotion[];
  primaryTag?: ProductTag;
  items: {
    itemType: 'product' | 'presentation';
    id: string;
    name: string;
    sku?: string;
    description: string | undefined;
    price: number;
    priceDiscount?: number;
    aliquot?: AliquotAmount;
    stockQty: number | null;
    cost?: number;
    targetMarginPct?: number;
    hasComposition: boolean;
    savingPrice?: boolean;
    savingPriceDiscount?: boolean;
    savingStockQty?: boolean;
    savingCost?: boolean;
    savingTargetMarginPct?: boolean;
    // Dimensiones físicas para envíos
    weight?: number;
    height?: number;
    length?: number;
    width?: number;
    savingWeight?: boolean;
    savingHeight?: boolean;
    savingLength?: boolean;
    savingWidth?: boolean;
  }[];
  searchableItemsPresentation: string;
  searchableItemsSku: string;
  searchableItemsPrice: string;
  searchableItemsPriceDiscount: string;
  searchableItemsCost: string;
  searchableItemsTargetMarginPct: string;
  searchableItemsAliquot: string;
  searchableItemsStock: string;
  searchableItemsWeight: string;
  searchableItemsHeight: string;
  searchableItemsLength: string;
  searchableItemsWidth: string;
  isOptionItem: boolean;
  productRef: Product;
  operation?: 'create' | 'update';
};

export type ProductImportValidationResult = {
  rowNumber: number;
  productData: ProductImportExcelData;
  isValid: boolean;
  errors?: ErrorObject[];
};

export type BulkActionType =
  | 'price'
  | 'priceDiscount'
  | 'stockQty'
  | 'group'
  | 'status'
  | 'category'
  | 'delete'
  | 'promotion'
  | 'targetMarginPct';

export type ZenriseDebtResponse = {
  status: string;
  message: string;
  data: ZenriseDebtData;
};

export type ZenrisePaymentMethodType = 'card' | 'cbu';

export type ZenriseDebtData = {
  daysRemaining: number;
  debt: number;
  total: number;
  priceCurrentPlan: number;
  subscription: SubscriptionInfo;
  slug: string;
  paymentMethodType: ZenrisePaymentMethodType;
  contact: {
    email: string;
    fullName: string;
  };
};

export interface ZenriseReactivateSubscription {
  subscriptionId: number;
  collectPendingInvoice: boolean;
  extraDays: number;
  creditCard: boolean;
  paymentToken: ZenriseReactivateSubscriptionPaymentToken;
}
interface ZenriseReactivateSubscriptionPaymentToken {
  bin: string;
  token: string;
  lastFourDigits: string;
  cardDueDate: string;
}

export interface ZenriseNewSubscriptionReactivation {
  subscriptionId: number;
  extraDays: number;
  paymentMethodType: ZenrisePaymentMethodType;
  paymentToken?: {
    bin: string;
    token: string;
    lastFourDigits: string;
  };
  card?: {
    cardNumber: string;
    expirationMonth: string;
    expirationYear: string;
    cardHolderName: string;
    securityCode: string;
    cardHolderDni: string;
    creditCard: boolean;
    email: string;
  };
  bankAccount?: ZenriseBankAccount;
  contact: {
    email: string;
    firstName: string;
    lastName: string;
    holderDocumentNumber: string;
    phoneNumber: string;
  };
}

export interface MercadopagoTokenResponse {
  success: boolean;
  message: string;
  data?: any;
}

export type PaymentLinkResponse = {
  paymentLink: string;
  id?: string;
};

export type UalaConfig = {
  user_name: string;
  client_id: string;
  client_secret_id: string;
  grant_type: string;
};

// Cloudflare IP Location
export type CfIpLocation = {
  country: string;
  city: string;
  continent: string;
  latitude: string;
  longitude: string;
  postalCode: string;
  metroCode: string;
  region: string;
  regionCode: string;
  timezone: string;
};

export type ExchangeRateResponse = {
  moneda: string;
  casa: string;
  nombre: string;
  compra: number;
  venta: number;
  fechaActualizacion: string;
};

export type Campaign = {
  campaignId: string;
  id?: string;
  adSetId: string;
  adId: string;
  establishmentId: string;
  establishmentName: string;
  establishmentSlug: string;
  budget: number;
  startDate: Date;
  endDate: Date;
  createdAt: Date;
  comments?: string;
  notificationSent?: boolean;
  activeDays: number;
  destination: 'STORE' | 'WHATSAPP';
  gender?: number[];
  ageMin?: number;
  ageMax?: number;
  categoryIds?: string[];
  paymentMethodId: string;
  // Campos adicionales para los datos de cada paso
  paymentIntentId?: string;
  catalogId?: string;
  catalogUrl?: string;
  feedId?: string;
  feedStatus?: string;
  productSetId?: string;
  customConversionId?: string;
  currentStep?: CampaignSteps;
  error?: string;
  lastUpdated?: Date;
  nextStep?: CampaignSteps;
  paymentCurrency?: 'USD' | 'ARS';
  exchangeRate?: number;
  totalAmountARS?: number;
  paymentInfo?: {
    gateway: 'stripe' | 'zenrise';
    // Para Stripe
    paymentIntentId?: string;
    clientSecret?: string;
    requires3DSecure?: boolean;
    // Para Zenrise
    paymentMethod?: string;
    paymentId?: number;
    amountPayed?: string;
    status?: string;
    invoiceId?: number;
    // Común
    currency?: 'USD' | 'ARS';
    exchangeRate?: number;
    totalAmountARS?: number;
  };
};

export enum CampaignSteps {
  CAMPAIGN_CREATION = 'CAMPAIGN_CREATION',
  PAYMENT = 'PAYMENT',
  PAYMENT_3D_SECURE_PENDING = 'PAYMENT_3D_SECURE_PENDING',
  CATALOG_SETUP_PENDING = 'CATALOG_SETUP_PENDING',
  CATALOG_SETUP_CREATE_CATALOG = 'CATALOG_SETUP_CREATE_CATALOG',
  CATALOG_SETUP_CREATE_FEED = 'CATALOG_SETUP_CREATE_FEED',
  CATALOG_SETUP_CHECK_FEED = 'CATALOG_SETUP_CHECK_FEED',
  CATALOG_SETUP_DEFAULT_PRODUCT_SET = 'CATALOG_SETUP_DEFAULT_PRODUCT_SET',
  CATALOG_SETUP_CUSTOM_CONVERSION = 'CATALOG_SETUP_CUSTOM_CONVERSION',
  CAMPAIGN_PRODUCT_SET_CREATION = 'CAMPAIGN_PRODUCT_SET_CREATION',
  FB_CREATE_CAMPAIGN_OBJECT = 'FB_CREATE_CAMPAIGN_OBJECT',
  FB_CREATE_ADSET_OBJECT = 'FB_CREATE_ADSET_OBJECT',
  FB_CREATE_AD_OBJECT = 'FB_CREATE_AD_OBJECT',
  FINALIZE_CAMPAIGN_DETAILS = 'FINALIZE_CAMPAIGN_DETAILS',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
}

export interface CampaignRequest {
  establishmentId: string;
  establishmentName: string;
  establishmentSlug: string;
  budget: number;
  activeDays: number;
  comments?: string;
  gender?: number[];
  ageMin?: number;
  ageMax?: number;
  categoryIds?: string[];
  paymentMethodId: string;
  destination?: 'STORE' | 'WHATSAPP';
  paymentCurrency?: 'USD' | 'ARS' | null;
  exchangeRate?: number;
  totalAmountARS?: number;
  cardData?: {
    cardNumber: string;
    cardName: string;
    expirationMonth: string;
    expirationYear: string;
    cvv: string;
    dni: string;
    creditCard: number; // 0=débito, 1=crédito
  };
}

export interface CampaignGrid {
  id?: string;
  createdAt: Date;
  adId: string;
  comments: string;
  endDate: Date;
  campaignId: string;
  adSetId: string;
  startDate: Date;
  establishmentId: string;
  budget: number;
  destination?: 'STORE' | 'WHATSAPP';
  facebookCampaign: CampaignGridFacebook;
  currentStep: CampaignSteps;
  insightsLastSync?: Date;
}

export interface CampaignGridFacebook {
  name: string;
  status: string;
  startDate: Date;
  endDate: Date;
  insights: CampaignGridFacebookInsights;
}

export interface CampaignGridFacebookInsights {
  spend: number;
  impressions: number;
  clicks: number;
  customConversionValue: number;
}
export type PricingError = {
  id: string;
  type: 'product' | 'presentation' | 'option-item';
  name: string;
  oldPrice: number;
  oldPriceDiscount: number;
  newPrice: number;
  newPriceDiscount: number;
  clonedFrom?: string;
  parentItem?: {
    id: string;
    name: string;
    clonedFrom?: string;
  };
};

export type HiddenProduct = {
  id: string;
  name: string;
  presentationId?: string;
};

export type ProductTag = {
  id: string;
  establishmentId: string;
  sortOrder: number;
  clonedFrom?: string;
  createdByOnboarding?: boolean;
  details: WithAltLanguages<
    {
      tag: string;
      color: string;
      created: Date;
      updated: Date;
    },
    'tag'
  >;
};

export type PackageType = {
  id: string;
  establishmentId: string;
  name: string;
  length: number;
  width: number;
  height: number;
  maxWeight: number;
  tareWeight: number;
  isDefault?: boolean;
  sortOrder: number;
  created: Date;
  updated: Date;
  clonedFrom?: string;
};

export type CalculateShipping = {
  establishmentId: string;
  endOrderDetails: EndOrderDetails;
};

export type ShippingInfo = {
  name: string;
  phone: string;
  address: Address;
};

export type ShippingEstimateRequest = {
  establishmentId: string;
  items: ShippingItem[];
  pickup: ShippingInfo;
  delivery: ShippingInfo;
  total: number;
  currencyCode: CurrencyCode;
  forceOndemand?: boolean;
};

export type ShippingItemType = 'COLD' | 'STANDARD' | 'FRAGILE';

export type ShippingItem = {
  description?: string;
  quantity: number;
  value: number;
  weight: number;
  height: number;
  length: number;
  width: number;
};

export type ShippingEstimate = {
  estimateId: string;
  providerName: string;
  offerId: string;
  price: number;
  deliveryTime: string;
  additionalInfo?: string;
  deliveryMode?: string;
  confirmationTimeLimit?: string;
  error?: string;
  outOfDeliveryZone?: boolean;
  blockOrder?: boolean;
  date: Date;
  // Campos específicos para Mi Correo
  deliveryType?: 'D' | 'S';
  productType?: string;
  agency?: string;
  serviceType?: 'ondemand' | 'dedicated';
};

export type DimensionsExceededInfo = {
  dimensionsExceeded: boolean;
  calculatedDimensions?: {
    length: number;
    width: number;
    height: number;
    weight?: number;
    weightKg?: number;
    maxDimension?: number;
    sumDimensions?: number;
  };
  exceededLimits?: {
    weight?: boolean;
    dimension?: boolean;
    sumDimensions?: boolean;
  };
  validationReason?: string;
};

export type CorreoArgentinoAgencyHours = {
  start: string;
  end: string;
} | null;

export type CorreoArgentinoAgency = {
  code: string;
  name: string;
  manager?: string;
  email?: string;
  phone?: string;
  services: {
    packageReception: boolean;
    pickupAvailability: boolean;
  };
  location: {
    address: {
      streetName: string;
      streetNumber: string;
      floor?: string | null;
      apartment?: string | null;
      locality: string;
      city: string;
      province: string;
      provinceCode: string;
      postalCode: string;
    };
    latitude: string;
    longitude: string;
  };
  hours: {
    sunday: CorreoArgentinoAgencyHours;
    monday: CorreoArgentinoAgencyHours;
    tuesday: CorreoArgentinoAgencyHours;
    wednesday: CorreoArgentinoAgencyHours;
    thursday: CorreoArgentinoAgencyHours;
    friday: CorreoArgentinoAgencyHours;
    saturday: CorreoArgentinoAgencyHours;
    holidays?: CorreoArgentinoAgencyHours;
  };
  status: 'ACTIVE' | 'INACTIVE';
};

export const ARGENTINA_PROVINCE_CODES = [
  'A', // Salta
  'B', // Provincia de Buenos Aires
  'C', // Ciudad Autónoma de Buenos Aires
  'D', // San Luis
  'E', // Entre Ríos
  'F', // La Rioja
  'G', // Santiago del Estero
  'H', // Chaco
  'J', // San Juan
  'K', // Catamarca
  'L', // La Pampa
  'M', // Mendoza
  'N', // Misiones
  'P', // Formosa
  'Q', // Neuquén
  'R', // Río Negro
  'S', // Santa Fe
  'T', // Tucumán
  'U', // Chubut
  'V', // Tierra del Fuego
  'W', // Corrientes
  'X', // Córdoba
  'Y', // Jujuy
  'Z', // Santa Cruz
] as const;

export type ArgentinaProvinceCode = (typeof ARGENTINA_PROVINCE_CODES)[number];

export const ARGENTINA_PROVINCES: { code: ArgentinaProvinceCode; name: string }[] = [
  { code: 'B', name: 'Buenos Aires' },
  { code: 'K', name: 'Catamarca' },
  { code: 'H', name: 'Chaco' },
  { code: 'U', name: 'Chubut' },
  { code: 'C', name: 'Ciudad Autónoma de Buenos Aires' },
  { code: 'X', name: 'Córdoba' },
  { code: 'W', name: 'Corrientes' },
  { code: 'E', name: 'Entre Ríos' },
  { code: 'P', name: 'Formosa' },
  { code: 'Y', name: 'Jujuy' },
  { code: 'L', name: 'La Pampa' },
  { code: 'F', name: 'La Rioja' },
  { code: 'M', name: 'Mendoza' },
  { code: 'N', name: 'Misiones' },
  { code: 'Q', name: 'Neuquén' },
  { code: 'R', name: 'Río Negro' },
  { code: 'A', name: 'Salta' },
  { code: 'J', name: 'San Juan' },
  { code: 'D', name: 'San Luis' },
  { code: 'Z', name: 'Santa Cruz' },
  { code: 'S', name: 'Santa Fe' },
  { code: 'G', name: 'Santiago del Estero' },
  { code: 'V', name: 'Tierra del Fuego' },
  { code: 'T', name: 'Tucumán' },
];

export function getStateIdFromPostalCode(postalCode: string): ArgentinaProvinceCode | undefined {
  if (!postalCode || postalCode.length === 0) return undefined;
  const firstChar = postalCode.charAt(0).toUpperCase() as ArgentinaProvinceCode;
  if (ARGENTINA_PROVINCE_CODES.includes(firstChar)) {
    return firstChar;
  }
  return undefined;
}

export function isValidArgentinaProvinceCode(code: string): code is ArgentinaProvinceCode {
  return ARGENTINA_PROVINCE_CODES.includes(code.toUpperCase() as ArgentinaProvinceCode);
}

export type ShippingStatus =
  | 'CONFIRMED'
  | 'CANCELLED'
  | 'IN_PROGRESS'
  | 'NEAR_PICKUP'
  | 'PICKED_UP'
  | 'NEAR_DROPOFF'
  | 'COMPLETED';

export type ShippingOrder = {
  confirmationCode: string;
  createdAt: Date;
  updatedAt: Date;
  status: ShippingStatus;
  providerName: string;
  shippingId: string;
  offerId?: string;
  estimatedDeliveryTime: string;
  distance?: number;
  deliveryMode?: string;
  trackingUrl?: string;
  supportUrl?: string;
  additionalInfo?: string;
  cancelCode?: string;
  cancelReason?: string;
  verificationCode?: string | null;
  serviceType?: 'ondemand' | 'dedicated';
  cancelError?: string;
  cancelAttemptedAt?: Date;
};

export type ShippingOrderHistory = {
  orderId: string;
  shippingOrder: ShippingOrder;
};

export type PedidosYaWebhookPayload = {
  topic: string;
  id: string;
  referenceId: string;
  generated: string;
  transmitted: string;
  data: {
    status:
      | 'CONFIRMED'
      | 'CANCELLED'
      | 'IN_PROGRESS'
      | 'NEAR_PICKUP'
      | 'PICKED_UP'
      | 'NEAR_DROPOFF'
      | 'COMPLETED';
    cancelCode?: string;
    cancelReason?: string;
    estimatedPickUpTime?: string;
    estimatedDropOffTime?: string;
  };
};

export type PuniyaWebhookPayload = {
  nroPed: number;
  estadoAPI: string;
  timeRetirar: string;
  observaciones?: string;
};

export type MultimediaFile = {
  id: string;
  establishmentId: string;
  details: {
    fileName: string;
    groupName: string;
    uri: string;
    mimeType: string;
    fileSize: number;
    created: Date;
    updated: Date;
  };
};

export type BatchOperationProgress<I, R> = {
  total: number;
  pending: I[];
  completed: { item: I; result: R }[];
  failed: { item: I; error: Error }[];
};

export type ConfirmShippingRequest = {
  establishmentId: string;
  shippingEstimate: ShippingEstimate;
  orderId: string;
};

export type CreateShippingRequest = {
  establishmentId: string;
  providerName: string;
  orderId: string;
  forceOndemand?: boolean;
};

export type CancelShippingRequest = {
  establishmentId: string;
  shippingOrder: ShippingOrder;
};

export type PuniyaShippingData = {
  accion: 'nuevo';
  idApi: number;
  tokenApi: string;
  valorProducto: number;
  timeRetirar: number;
  rango: 1 | 2 | 3;
  acargo: 0 | 1;
  ciudadOrigen: string;
  calleOrigen: string;
  deptoOrigen?: string;
  alturaOrigen: number;
  telOrigen: number;
  cordsOrigen?: string;
  urlOrigen?: string;
  comentarioOrigen?: string;
  ciudadDestino: string;
  calleDestino: string;
  deptoDestino?: string;
  alturaDestino: number;
  telDestino: number;
  cordsDestino?: string;
  urlDestino?: string;
};
export type CabifyWebhookPayload = {
  id: string;
  external_id?: string;
  event_time: string;
  state: string;
  failure_reason?: string;
  delivery_attempt?: {
    id_proof_of_delivery?: {
      recipient_name?: string;
      recipient_id_number?: string;
    };
    photo_proof_of_delivery?: {
      photo_url?: string;
    };
    comment_proof_of_delivery?: {
      comment?: string;
    };
    fail_reason?: string;
    support_ticket?: string;
    feedback?: string;
  };
  tracking?: {
    eta_to_accept?: number;
    location?: {
      lat: number;
      lon: number;
    };
    tracking_url?: string;
  };
  asset?: {
    reg_plate?: string;
    name: string;
    color: string;
    asset_kind: string;
  };
  driver?: {
    photo_url: string;
    name: string;
    phone: string;
  };
};

export type InstagramIssueConfig = {
  android: boolean;
  ios: boolean;
};

export const customerStages = ['A', 'B', 'C', 'DORMANT'] as const;
export type CustomerStage = (typeof customerStages)[number];
export type CustomerEffectiveStage = CustomerStage | 'RECOVERY';
export const recoveryReasons = ['PAYMENT_FAILED', 'CANCELLED'] as const;
export type RecoveryReason = (typeof recoveryReasons)[number];

export const achievementKeys = [
  'CONFIGURATION',
  'CATALOG_BASE',
  'ENRICHED_CATALOG',
  'OPERATES_WITH_PEDIX',
  'SALES_FEATURES',
  'MEASUREMENT',
  'AHA',
  'SELLS_TODAY',
  'SUSTAINS',
  'GROWS',
  'ONLINE_PAYMENT',
] as const;
export type AchievementKey = (typeof achievementKeys)[number];
export const achievementStates = ['MET', 'NOT_MET', 'UNREACHABLE'] as const;
export type AchievementState = (typeof achievementStates)[number];

export const operationalModules = [
  'CASH_REGISTER',
  'ORDER_CENTER',
  'STOCK',
  'INVENTORY',
  'SUPPLIES',
  'COSTS',
  'BILLING',
  'TABLES',
] as const;
export type OperationalModule = (typeof operationalModules)[number];
export const moduleAvailabilities = ['PLAN', 'TRIAL', 'TRIAL_AVAILABLE', 'TRIAL_EXPIRED'] as const;
export type ModuleAvailability = (typeof moduleAvailabilities)[number];
export const nextModuleActions = ['USE', 'START_TRIAL'] as const;
export type NextModuleAction = (typeof nextModuleActions)[number];

export type CustomerScoreStage =
  | { stage: 'RECOVERY'; recoveryReason: RecoveryReason }
  | { stage: CustomerStage; recoveryReason: null };

export type CustomerScoreDetails = CustomerScoreStage & {
  sourceComputedAt: Date;
  commercialStage: CustomerStage;
  linkOrdersLast30Days: number;
  storeVisitsLast7Days: number;
  trafficChannelsLast30Days: number;
  dormantRecoverable: boolean;
  scorePct: number;
  achievements: Record<AchievementKey, AchievementState>;
  firstMissingAchievement: AchievementKey | null;
  onlinePayment: { mercadopago: boolean; bankTransfer: boolean };
  modules: Partial<
    Record<
      OperationalModule,
      {
        availability: ModuleAvailability;
        activeDaysLast30: number;
        lastUsedAt: Date | null;
        inHabit: boolean;
      }
    >
  >;
  nextModule: { module: OperationalModule; action: NextModuleAction } | null;
  milestones: {
    firstOrderAt: Date | null;
    thirdOrderAt: Date | null;
    ahaAt: Date | null;
    firstExternalOrderAt: Date | null;
    ahaExternalAt: Date | null;
    firstSalesHabitAt: Date | null;
    firstOperationalHabitAt: Date | null;
  };
  signals: {
    stageChangedFrom: CustomerEffectiveStage | null;
    lostHabitModules: OperationalModule[];
    volumeDropPct: number | null;
  };
};

export type CustomerScore = {
  establishmentId: string;
  updated: Date;
  details: CustomerScoreDetails;
};

export interface NotificationVariables {
  customerName: string;
  username: string;
  password: string;
  adminUrl: string;
  establishmentUrl: string;
}

export interface EstablishmentForNotification {
  id: string;
  slug: string;
  currencyCode?: CurrencyCode;
  countryCode?: Country;
  addressDetails?: AddressDetails;
  contactInfo?: {
    email?: string;
    phone?: string;
    firstName?: string;
    name?: string;
  };
  subscription?: {
    status: string;
    isTrial?: boolean;
    createdAt?: Date;
  };
  created?: Date;
  account?: {
    username: string;
    password: string;
  };
}

export type AuthMfaCode = {
  uid: string;
  details: {
    created: Date;
    updated: Date;
    verified?: Date;
    code: string;
    userType: 'establishment' | 'invited';
    retries?: number;
    contact?: string;
  };
};

export type AuthMfaSendCodeResponse = {
  uid: string;
  slug: string;
  exception?: 'resend-code-exception' | 'send-new-code-exception';
  dateSent: Date;
  retries: number;
  minutesForResendCode: number;
  minutesForSendNewCode: number;
  maxRetries: number;
  contact?: string;
};

export type RemoteConfigSecurity = {
  enforceMfa: boolean;
};
